import { collection, doc, onSnapshot, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { db } from "../config/firebase";
import { getAvatarUrl, getUserByEmail } from "../config/users";
import { UserAuth } from "./AuthContext";

const PresenceContext = createContext();

export const PresenceContextProvider = ({ children }) => {
  const { user } = UserAuth();
  const [currentProject, setCurrentProject] = useState(null);
  const currentProjectRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    currentProjectRef.current = currentProject;
  }, [currentProject]);

  // Update presence in Firestore - memoized to prevent infinite loops
  const updatePresence = useCallback(async (projectId = null, projectName = null) => {
    if (!user?.email) return;

    const userConfig = getUserByEmail(user.email);
    const presenceRef = doc(db, "presence", user.email);

    try {
      await setDoc(
        presenceRef,
        {
          email: user.email,
          name: userConfig?.name || user.displayName || "Unknown",
          avatar: getAvatarUrl(user.email, user.photoURL),
          lastSeen: serverTimestamp(),
          currentProject: projectId
            ? { id: projectId, name: projectName || projectId }
            : null,
        },
        { merge: true }
      );
      console.log("✓ Presence updated:", user.email, projectId || "(no project)");
    } catch (error) {
      // Silently fail if Firestore isn't set up
      if (error.code === "not-found") {
        console.log("Firestore not configured - presence tracking disabled");
      } else {
        console.error("✗ Error updating presence:", error.message);
      }
    }
  }, [user?.email, user?.displayName, user?.photoURL]);

  // Set current project being viewed
  const setViewingProject = useCallback((projectId, projectName = null) => {
    setCurrentProject({ id: projectId, name: projectName });
    updatePresence(projectId, projectName);
  }, [updatePresence]);

  // Clear current project
  const clearViewingProject = useCallback(() => {
    setCurrentProject(null);
    updatePresence(null, null);
  }, [updatePresence]);

  // Update presence on login and tab focus
  useEffect(() => {
    if (!user?.email) return;

    // Update presence on login (once)
    updatePresence(currentProjectRef.current?.id, currentProjectRef.current?.name);

    // Update when tab becomes visible again
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updatePresence(currentProjectRef.current?.id, currentProjectRef.current?.name);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user?.email, updatePresence]);

  return (
    <PresenceContext.Provider
      value={{
        currentProject,
        setViewingProject,
        clearViewingProject,
      }}
    >
      {children}
    </PresenceContext.Provider>
  );
};

export const usePresence = () => {
  return useContext(PresenceContext);
};

// Hook to get collaborators viewing the same project (excluding current user)
export const useProjectCollaborators = projectId => {
  const { user } = UserAuth();
  const [collaborators, setCollaborators] = useState([]);

  useEffect(() => {
    if (!projectId) {
      setCollaborators([]);
      return;
    }

    const presenceRef = collection(db, "presence");
    const q = query(presenceRef, where("currentProject.id", "==", projectId));

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const viewers = snapshot.docs
          .map(doc => ({
            email: doc.id,
            ...doc.data(),
          }))
          .filter(viewer => viewer.email !== user?.email);

        setCollaborators(viewers);
      },
      error => {
        // Silently fail if Firestore isn't set up
        if (error.code !== "not-found") {
          console.error("Error listening to collaborators:", error.message);
        }
      }
    );

    return () => unsubscribe();
  }, [projectId, user?.email]);

  return collaborators;
};

// Hook to get all users with their last seen (for admin page)
export const useAllUsersPresence = () => {
  const [usersPresence, setUsersPresence] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const presenceCollection = collection(db, "presence");

    const unsubscribe = onSnapshot(
      presenceCollection,
      snapshot => {
        const presenceData = snapshot.docs.map(doc => ({
          email: doc.id,
          ...doc.data(),
          lastSeen: doc.data().lastSeen?.toDate() || null,
        }));
        setUsersPresence(presenceData);
        setLoading(false);
      },
      error => {
        // Silently fail if Firestore isn't set up
        if (error.code !== "not-found") {
          console.error("Error listening to presence:", error.message);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { usersPresence, loading };
};
